import { DeliveryType } from "@omni/types/api/product/bff/shared/enums/deliveryType.enum";
import { ProductVisibilityType } from "@omni/types/api/product/bff/shared/enums/productVisibilityType.enum";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { brandMapper } from "@/components/ProductPage/mappers/brand.mapper";
import { deliveryItemMapper } from "@/components/ProductPage/mappers/deliveryItem.mapper";
import { getBoxCount } from "@/redux/modules/api/data/boxCount/boxCount.selectors";
import * as ProductComparisonSkuListSelectors from "@/redux/modules/value/productComparisonSkuList/productComparisonSkuList.selectors";
import { getProductDetail } from "@/redux/modules/value/productDetail/productDetail.selectors";
import * as ProductUtils from "@/utils/product.utils";

import { useECommerceContext } from "../context/eCommerce.context";
export const useECommerceProduct = () => {
    const { product } = useECommerceContext();
    const { deliveryType } = useSelector(getProductDetail);
    const productComparisonSkuList = useSelector(
        ProductComparisonSkuListSelectors.getSkuList
    );
    const boxCount = useSelector(getBoxCount(product.sku));

    const [isBoxSoldOut, setIsBoxSoldOut] = useState(false);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        setIsBoxSoldOut(
            product?.boxArticle && boxCount !== undefined && boxCount <= 0
        );
    }, [boxCount]);

    const visibilityType = (product?.productVisibilityType ||
        ProductVisibilityType.Default) as ProductVisibilityType;

    const superCategory = product.superCategories?.length
        ? product.superCategories[0]
        : undefined;

    const minimumOrderQuantity =
        deliveryType === DeliveryType.ClickAndCollect
            ? 1
            : product.minimumOrderQuantity;

    const roundingValue =
        deliveryType === DeliveryType.ClickAndCollect
            ? 1
            : product.roundingValue;

    const isAdditionalServiceProduct =
        ProductUtils.isAdditionalService(product);

    const noDelivery =
        visibilityType === ProductVisibilityType.NotAvailable ||
        visibilityType === ProductVisibilityType.TemporaryNotAvailable ||
        visibilityType === ProductVisibilityType.Free;

    const deliveryTypes = product.deliveryTypes || [];

    const deliveryOptions =
        (deliveryTypes || []).map((type: string) =>
            deliveryItemMapper(type, product)
        ) || [];

    const discountPercentage = Math.floor(
        product?.price?.discountPercentage || 0
    );

    return {
        sku: product.sku,
        currentPrice: product?.price?.currentPrice || null,
        currentConversionPrice: product?.price?.currentConversionPrice || null,
        conversionUnit: product?.price?.conversionUnit || null,
        discountPercentage,
        strikethroughPrice: product?.price?.strikethroughPrice || null,
        strikethroughConversionPrice:
            product?.price?.strikethroughConversionPrice || null,
        salesIncentives: product.salesIncentives || [],
        isBoxSoldOut,
        brand: brandMapper(product.brand),
        category: superCategory,
        minimumOrderQuantity,
        roundingValue,
        promotions: product?.price?.promotions || [],
        isAdditionalService: isAdditionalServiceProduct,
        isBoxArticle: !!product.boxArticle,
        visibilityType: visibilityType,
        deliveryTypes,
        hasDelivery: !noDelivery,
        deliveryOptions,
        isLimitedStock: product.limitedStock,
        features: product.features || [],
        boxLogisticInformation: product.boxLogisticInformation,
        montageParents: product.montageParents || [],
        montages: product.references.montages || [],
        isEveryDayLowPrice: product.everyDayLowPrice || false,
        isWebOnly: product.webOnly,
        variantGroups: product.productVariants || [],
        recupel: product.recupel,
        analytics: product.analytics,
        hasComparisonSkus:
            productComparisonSkuList && productComparisonSkuList.length > 0,
        tieredPrices: product.price?.tieredPrices || []
    };
};
